import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AlertCircle, Lock, User, Eye, EyeOff, Shield } from 'lucide-react';
import './Admin.css';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm]           = useState({ username: '', password: '' });
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPwd, setShowPwd]     = useState(false);

  React.useEffect(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }, []);

  const validate = () => {
    const e = {};
    if (!form.username.trim()) e.username = 'Username is required';
    if (!form.password)        e.password = 'Password is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await login(form);
      navigate('/admin/dashboard');
    } catch (err) {
      const status = err.response?.status;
      const errCode = err.code;
      if (status === 401 || status === 403) {
        setServerError('Invalid credentials. Please try again.');
      } else if (status === 500) {
        setServerError('Server error. Please contact support.');
      } else if (errCode === 'ERR_NETWORK' || errCode === 'ECONNREFUSED') {
        setServerError('Unable to connect. Please try again later.');
      } else if (errCode === 'ECONNABORTED') {
        setServerError('Request timed out. Please try again.');
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => {
    setForm(p => ({...p, [k]: v}));
    if (errors[k]) setErrors(p => ({...p, [k]: ''}));
    setServerError('');
  };

  return (
    <div className="admin-login">
      <div className="admin-login__box">
        <div className="admin-login__header">
          <div className="admin-login__icon"><Shield size={28} color="var(--primary)"/></div>
          <h2>Admin Panel</h2>
          <p className="login-sub">Smart Property Solutions</p>
        </div>

        {serverError && (
          <div className="login-error"><AlertCircle size={15}/> {serverError}</div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label><User size={11} style={{marginRight:5}}/>Username</label>
            <input
              type="text" placeholder="Enter username"
              value={form.username} onChange={e => set('username', e.target.value)}
              className={errors.username ? 'input-error' : ''}
              autoFocus autoComplete="username"
            />
            {errors.username && <span className="field-error"><AlertCircle size={11}/> {errors.username}</span>}
          </div>

          <div className="form-group">
            <label><Lock size={11} style={{marginRight:5}}/>Password</label>
            <div className="password-wrap">
              <input
                type={showPwd ? 'text' : 'password'} placeholder="Enter password"
                value={form.password} onChange={e => set('password', e.target.value)}
                className={errors.password ? 'input-error' : ''}
                autoComplete="current-password"
              />
              <button type="button" className="pwd-eye" onClick={() => setShowPwd(p => !p)}>
                {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
            {errors.password && <span className="field-error"><AlertCircle size={11}/> {errors.password}</span>}
          </div>

          <button type="submit" className="btn-primary admin-login__btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="admin-login__back"><Link to="/">Back to website</Link></div>
        <div className="admin-login__back" style={{marginTop:'8px'}}><Link to="/admin/reset-password">Forgot password?</Link></div>
      </div>
    </div>
  );
};

export default AdminLogin;
